import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";
import usePortal from "../../../../hooks/usePortal";

// Pin the bar when its in-flow position would leave it within this distance
// of (or beyond) the bottom of the viewport.
const PIN_THRESHOLD_PX = 50;

const SCROLL_DEBOUNCE_MS = 300;

export function useBulkActionsBarScrolling() {
  const [isFixed, setIsFixed] = useState(false);
  const reference = useRef<HTMLDivElement | null>(null);
  const target = usePortal("bulk-actions-bar", true);

  const measure = useCallback(() => {
    const node = reference.current;
    // Detached nodes (pre-portal-append) measure as all zeros — never
    // decide pin state from that geometry.
    if (!node?.isConnected) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const parentRect = node.parentElement?.getBoundingClientRect();
    const distanceFromBottom = globalThis.innerHeight - rect.bottom;
    const parentDistanceFromBottom = parentRect
      ? globalThis.innerHeight - parentRect.bottom
      : undefined;

    // While pinned, the bar's own rect always sits near the viewport
    // bottom, so the unpin decision reads the in-flow anchor instead.
    setIsFixed((currentlyFixed) => {
      if (!currentlyFixed) {
        return distanceFromBottom < PIN_THRESHOLD_PX;
      }
      if (parentDistanceFromBottom === undefined) {
        return currentlyFixed;
      }
      return parentDistanceFromBottom < PIN_THRESHOLD_PX;
    });
  }, []);

  // Initial measurement; must stay declared after the usePortal call.
  // usePortal appends the subtree in its own (earlier) effect, so this
  // measures attached geometry, and the pin state commits before the bar's
  // first paint. A pre-paint layout effect would run before the append and
  // could only ever see detached zero geometry.
  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const debouncedMeasure = debounce(measure, SCROLL_DEBOUNCE_MS);
    globalThis.addEventListener("scroll", debouncedMeasure);
    globalThis.addEventListener("resize", debouncedMeasure);

    return () => {
      debouncedMeasure.cancel();
      globalThis.removeEventListener("scroll", debouncedMeasure);
      globalThis.removeEventListener("resize", debouncedMeasure);
    };
  }, [measure]);

  return { isFixed, ref: reference, target };
}
